/**
 * C-GTCC-VIEW-DIAG-AIA1 — GTCC view "Diagnostics", 1:1 after
 * `GTCC_REFERENCE_2026-09-01.html`, `div[data-v="diag"]` (lines 629-679).
 *
 * Layout of the reference, element for element:
 *   - head "Diagnostics" with the freshness badge and the `.sub` line,
 *   - `.grid-main`, leading column — "Ende-zu-Ende-Pfad-Probe" and
 *     "Datenqualitäts-Prüfer je Fahrzeug",
 *   - `.grid-main`, trailing column — "Auth-Diagnose", "Berechtigungs-Prüfer",
 *     "Rohpaket-Decoder", "Weitere Sonden".
 *
 * P19 of the program document folds the old views into this one: Smoke
 * Dashboard, Recent Errors, Permission Diagnostics and the Diagnostics
 * snapshot. The reference only *links* to them from "Weitere Sonden"; inside
 * the deck there is nowhere left to link to, so Smoke and Recent Errors are
 * rendered as panels of this view and their rows of "Weitere Sonden" name the
 * panel instead of a target. The Mail test send is the one exception: it is an
 * existing TEST-marked **write** and keeps living in its own view (`mail`), so
 * the row navigates there rather than opening a second send path here.
 *
 * A-GTCC-VIEW-DIAG2, S3 — the presentation follows the reference where it did
 * not before (`docs/design/gtcc-screens/E-GTCC-ACCEPT2/ABWEICHUNGEN.md`,
 * section "diag"):
 *   - the path probe has the reference's two columns; the extra "VERDIKT"
 *     column, which only ever repeated "nicht messbar", is gone,
 *   - the Service-/Config-Snapshot is no longer a panel of its own. The
 *     reference lists it as one row of "Weitere Sonden" reading
 *     "kopieren →", and that is what it is now: the payload is still loaded
 *     and still copied verbatim, but it no longer occupies a panel the
 *     reference does not have,
 *   - Auth-Diagnose, Berechtigungs-Prüfer and Rohpaket-Decoder are rows, not
 *     forms: no text field, no select, no button. The reference states each
 *     probe as a sentence ending in "prüfen →" / "decodieren →", and the two
 *     probes that can answer locally do so on activation (role catalog, packet
 *     from the clipboard) instead of behind a form.
 *
 * Honesty (Owner P8/P9/P10): every value on this page comes from a payload or
 * from a pure local computation over the role catalog / the decoded packet.
 * The probes without a source keep their structure and read "nicht messbar",
 * naming what is missing — an absent endpoint, or the selection the existing
 * endpoint needs. The head no longer promises an audit-log entry per probe
 * run: probe 21 of `SONDEN.md` found no source for that promise, and two of
 * these probes make no request at all, so the claim was untrue for them.
 * Nothing here derives an age from the local clock: the payloads of the old
 * console carry no freshness, so the badge says "Alter nicht messbar".
 *
 * Read-only by construction: every request of this module is a `gtccGet` or an
 * existing admin GET. The two interactive probes (Berechtigungs-Prüfer,
 * Rohpaket-Decoder) make no request at all.
 */
import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useState, type ReactNode } from "react";

import {
  DIAG_LINKS_KEY,
  DIAG_MAIL_HEALTH_KEY,
  DIAG_RECENT_ERRORS_KEY,
  DIAG_RECENT_ERRORS_LIMIT,
  DIAG_SNAPSHOT_KEY,
  fetchDiagDeviceState,
  fetchDiagGatewayView,
  fetchDiagLinks,
  fetchDiagMailHealth,
  fetchDiagRecentErrors,
  fetchDiagSnapshot,
  type DiagDeviceStateDto,
  type DiagDeviceStateOutcome,
  type DiagGatewayViewDto,
  type DiagLinksDto,
  type DiagMailHealthDto,
  type DiagRecentErrorsDto,
  type DiagSnapshotDto,
} from "../../api/diag.js";
import type { GtccResult } from "../../api/gtcc-result.js";
import { writeGtccViewKeyToLocation } from "../../deep-link-url.js"; // Blatt-Modul: bricht den Zyklus registry -> DiagView -> deep-link -> registry.
import {
  ControlActionRow,
  ControlFreshBadge,
  ControlHead,
  ControlMono,
  ControlNotMeasurable,
  ControlNote,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  ControlTextAction,
  GRID_COLUMN_STYLE,
  GRID_MAIN_CLASS,
  HOST_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  asList,
  formatInteger,
  type ControlTone,
} from "../control/ControlPrimitives.js";
import {
  GTCC_SMOKE_PROBES,
  probeTone,
  tallySmokeQueries,
  useGtccSmokeQueries,
  type GtccSmokeProbeKey,
  type GtccSmokeTone,
} from "../smoke/useGtccSmoke.js";

import { decodeCodec8Frame, type Codec8DecodeResult } from "./codec8-decode.js";
import {
  PERMISSION_AREAS,
  PERMISSION_ROLE_IDS,
  checkPermissionRole,
  type PermissionAccess,
  type PermissionRoleCheck,
} from "./permission-matrix.js";

/* ---------------------------------------------------------------- queries */

/** Ticket floor of the control views: never refetch faster than 30 s. */
const DIAG_STALE_MS = 30_000;
const DIAG_REFETCH_MS = 60_000;

const DIAG_QUERY_OPTIONS = {
  staleTime: DIAG_STALE_MS,
  refetchInterval: DIAG_REFETCH_MS,
  refetchOnWindowFocus: false,
} as const;

function dataOf<T>(query: UseQueryResult<GtccResult<T>>): T | null {
  return query.data !== undefined && query.data.ok ? query.data.data : null;
}

/** How many of the 200 buffered errors the panel prints before it truncates. */
const DIAG_RECENT_VISIBLE = 25;

/* ---------------------------------------------------------------- pieces */

/** The `.dot` of the old console's smoke tones, in this view's vocabulary. */
function toneOfSmoke(tone: GtccSmokeTone): "ok" | "warn" | "crit" {
  return tone === "green" ? "ok" : tone === "yellow" ? "warn" : "crit";
}

function toneOfAccess(access: PermissionAccess): ControlTone {
  return access === "write" ? "warn" : access === "read" ? "ok" : "muted";
}

/* ------------------------------------------------------------------- view */

/** The four stations of the reference's path probe, in its own order. */
const DIAG_PATH_STATIONS = ["device", "gateway", "database", "map"] as const;

/**
 * Warum eine Station heute nicht messbar ist — zwei verschiedene Saetze, weil
 * es zwei verschiedene Gruende sind (S2 hat den ersten veraendert):
 *
 *   - `no_selection` — eine lesende Route existiert jetzt
 *     (`/v1/admin/gtcc/diag/device-state`, `…/gateway-view`), sie braucht aber
 *     ein Geraet. Diese Ansicht hat keine Geraeteauswahl, also liegt keine
 *     Messung vor. „Kein Endpunkt" waere hier ab S2 falsch.
 *   - `no_endpoint` — fuer die Stationen „Datenbank (Store)" (Sonde 3) und
 *     „Karte / App" (Sonde 4) gibt es in diesem Repository keine lesende
 *     Route. Ihre Tatsachen stehen in Tabellen unter FORCE RLS; eine
 *     plattformweite Lesung braucht eine neue SECURITY-DEFINER-Funktion, und
 *     das ist eine Owner-Entscheidung ausserhalb dieses Pakets
 *     (`docs/design/gtcc-screens/A-GTCC-VIEW-DIAG2/SONDEN.md`, Abschnitt
 *     „Offene Owner-Entscheidung").
 */
/** The four checks of the reference's "3-km-Lehre als Werkzeug" panel. */
const DIAG_QUALITY_CHECKS = ["jumps", "scatter", "filter", "odometer"] as const;

export function DiagView(): ReactNode {
  const { t, locale } = useLocale();

  const snapshotQuery = useQuery({
    queryKey: DIAG_SNAPSHOT_KEY,
    queryFn: ({ signal }) => fetchDiagSnapshot(signal),
    ...DIAG_QUERY_OPTIONS,
  });
  const linksQuery = useQuery({
    queryKey: DIAG_LINKS_KEY,
    queryFn: ({ signal }) => fetchDiagLinks(signal),
    ...DIAG_QUERY_OPTIONS,
  });
  const recentErrorsQuery = useQuery({
    queryKey: DIAG_RECENT_ERRORS_KEY,
    queryFn: ({ signal }) => fetchDiagRecentErrors(signal),
    ...DIAG_QUERY_OPTIONS,
  });
  const mailHealthQuery = useQuery({
    queryKey: DIAG_MAIL_HEALTH_KEY,
    queryFn: ({ signal }) => fetchDiagMailHealth(signal),
    ...DIAG_QUERY_OPTIONS,
  });
  /* P19: das Smoke Dashboard ist jetzt ein Panel dieser Ansicht. Die acht
     Sonden behalten ihre Schluessel `["gtcc","smoke",…]`, damit die Registry
     den alten Schluessel `smoke` unveraendert auf diesen View zeigen kann. */
  const smokeQueries = useGtccSmokeQueries();

  const snapshot: DiagSnapshotDto | null = dataOf(snapshotQuery);
  const links: DiagLinksDto | null = dataOf(linksQuery);
  const recentErrors: DiagRecentErrorsDto | null = dataOf(recentErrorsQuery);
  const mailHealth: DiagMailHealthDto | null = dataOf(mailHealthQuery);

  const queries = [snapshotQuery, linksQuery, recentErrorsQuery, mailHealthQuery];
  /* Nur `isPending`: eine mit einem Fehler gesettelte Query traegt kein
     `data` — wer sie als „laedt noch" zaehlt, dreht den Spinner ewig. */
  const allPending = queries.every((query) => query.isPending);
  const anyLoaded =
    snapshot !== null || links !== null || recentErrors !== null || mailHealth !== null;
  const deniedOnly = queries.some(
    (query) => query.data !== undefined && !query.data.ok && query.data.kind === "forbidden",
  );

  const goTo = useCallback((key: string) => {
    writeGtccViewKeyToLocation(key, false, import.meta.env.BASE_URL);
    if (typeof window !== "undefined") window.dispatchEvent(new PopStateEvent("popstate"));
  }, []);

  const head = (
    <ControlHead
      testId="gtcc-diag-head"
      title={t("admin.gtcc.diag.head.title")}
      subtitle={t("admin.gtcc.diag.head.subtitle")}
      fresh={
        /* Keine dieser Nutzlasten traegt `freshnessSeconds`; ein Alter aus der
           lokalen Uhr waere erfunden (P10). */
        <ControlFreshBadge
          testId="gtcc-diag-fresh"
          text={t("admin.gtcc.diag.fresh.unmeasurable")}
        />
      }
    />
  );

  if (allPending) {
    return (
      <Stack gap={3} data-testid="gtcc-view-diag">
        {head}
        <div data-testid="gtcc-diag-loading">
          <LoadingState label={t("admin.gtcc.diag.loading")} />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded && deniedOnly) {
    return (
      <Stack gap={3} data-testid="gtcc-view-diag">
        {head}
        <div data-testid="gtcc-diag-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.diag.forbidden.title")}
            body={t("admin.gtcc.diag.forbidden.body")}
          />
        </div>
      </Stack>
    );
  }

  if (!anyLoaded) {
    return (
      <Stack gap={3} data-testid="gtcc-view-diag">
        {head}
        <div data-testid="gtcc-diag-failure">
          <ErrorState
            severity="warn"
            title={t("admin.gtcc.diag.error.title")}
            body={t("admin.gtcc.diag.failure.body")}
          />
        </div>
      </Stack>
    );
  }

  return (
    <Stack gap={3} data-testid="gtcc-view-diag">
      {head}
      <div className={GRID_MAIN_CLASS}>
        <div style={GRID_COLUMN_STYLE}>
          <PathProbePanel />
          <DataQualityPanel />
          <RecentErrorsPanel errors={recentErrors} locale={locale} />
        </div>
        <div style={GRID_COLUMN_STYLE}>
          <AuthDiagnosisPanel />
          <PermissionCheckerPanel />
          <PacketDecoderPanel />
          <MoreProbesPanel
            mailHealth={mailHealth}
            snapshot={snapshot}
            links={links}
            onOpenMail={() => goTo("mail")}
          />
          <SmokePanel queries={smokeQueries} locale={locale} />
        </div>
      </div>
    </Stack>
  );
}

/* ---------------------------------------------------- 1 · Ende-zu-Ende-Pfad */

const PATH_INPUT_STYLE = {
  background: "var(--gt-sys-surface)",
  border: "1px solid var(--gt-sys-border)",
  borderRadius: "var(--gt-sys-radius-sm)",
  color: "var(--gt-sys-text)",
  padding: "var(--gt-sys-space-2)",
  fontFamily: "var(--gt-sys-font-family-mono)",
  fontSize: "var(--gt-sys-font-size-sm)",
  minWidth: "12rem",
} as const;

/**
 * The one payload field every failed `GtccResult` carries in common — a
 * caller renders it verbatim instead of collapsing every non-2xx into the
 * same "nicht messbar" (P9: a source limitation is a fact of the payload).
 */
function pathProbeErrorLabel(
  t: ReturnType<typeof useLocale>["t"],
  result: GtccResult<unknown> | undefined,
): string | null {
  if (result === undefined || result.ok) return null;
  if (result.kind === "disabled") return t("admin.gtcc.diag.path.select.disabled");
  if (result.kind === "forbidden") return t("admin.gtcc.diag.path.select.forbidden");
  if (result.kind === "upstream") return t("admin.gtcc.diag.path.select.upstream");
  return t("admin.gtcc.diag.path.select.transport");
}

function DevicePathFinding(props: {
  selected: boolean;
  query: UseQueryResult<GtccResult<DiagDeviceStateDto>>;
  device: DiagDeviceStateDto | null;
}): ReactNode {
  const { t, locale } = useLocale();
  if (!props.selected) {
    return <ControlNotMeasurable label={t("admin.gtcc.diag.no_selection")} />;
  }
  if (props.query.isPending) {
    return <ControlMono tone="muted">{t("admin.gtcc.diag.path.select.loading")}</ControlMono>;
  }
  const errorLabel = pathProbeErrorLabel(t, props.query.data);
  if (errorLabel !== null) {
    return <ControlNotMeasurable label={errorLabel} />;
  }
  const device = props.device;
  if (device === null) {
    return <ControlMono tone="muted">{t("admin.gtcc.diag.path.select.loading")}</ControlMono>;
  }
  if (device.outcome === "not_in_capped_list") {
    return (
      <ControlNotMeasurable
        label={t("admin.gtcc.diag.path.device.capped", {
          total: formatInteger(device.list.total, locale),
        })}
      />
    );
  }
  if (device.outcome === "not_found" || device.device === null) {
    return (
      <ControlMono tone="muted">
        {t("admin.gtcc.diag.path.device.not_found", {
          total: formatInteger(device.list.total, locale),
        })}
      </ControlMono>
    );
  }
  return (
    <ControlMono tone="ok">
      {t("admin.gtcc.diag.path.device.found", {
        identifier: device.device.identifier,
        lastPacket: device.device.lastPacketAt ?? t("admin.gtcc.diag.path.value.never"),
      })}
    </ControlMono>
  );
}

function GatewayPathFinding(props: {
  deviceOutcome: DiagDeviceStateOutcome | null;
  identifier: string | null;
  query: UseQueryResult<GtccResult<DiagGatewayViewDto>>;
  gateway: DiagGatewayViewDto | null;
}): ReactNode {
  const { t, locale } = useLocale();
  if (props.identifier === null) {
    return (
      <ControlNotMeasurable
        label={
          props.deviceOutcome === null
            ? t("admin.gtcc.diag.no_selection")
            : t("admin.gtcc.diag.path.gateway.needs_device")
        }
      />
    );
  }
  if (props.query.isPending) {
    return <ControlMono tone="muted">{t("admin.gtcc.diag.path.select.loading")}</ControlMono>;
  }
  const errorLabel = pathProbeErrorLabel(t, props.query.data);
  if (errorLabel !== null) {
    return <ControlNotMeasurable label={errorLabel} />;
  }
  const gateway = props.gateway;
  if (gateway === null) {
    return <ControlMono tone="muted">{t("admin.gtcc.diag.path.select.loading")}</ControlMono>;
  }
  if (
    gateway.ingest.outcome === "not_in_capped_list" ||
    gateway.rejections.outcome === "not_in_capped_list"
  ) {
    return <ControlNotMeasurable label={t("admin.gtcc.diag.path.gateway.capped")} />;
  }
  const ingestDevice = gateway.ingest.outcome === "found" ? gateway.ingest.device : null;
  if (ingestDevice !== null) {
    return (
      <ControlMono tone="ok">
        {t("admin.gtcc.diag.path.gateway.found", {
          accepted: formatInteger(ingestDevice.accepted, locale),
          rateLimited: formatInteger(ingestDevice.rateLimited, locale),
          lastAccepted: ingestDevice.lastAccepted ?? t("admin.gtcc.diag.path.value.never"),
        })}
      </ControlMono>
    );
  }
  const rejection = gateway.rejections.outcome === "found" ? gateway.rejections.entry : null;
  if (rejection !== null) {
    return (
      <ControlMono tone="warn">
        {t("admin.gtcc.diag.path.gateway.rejected", {
          reason: rejection.reason,
          attempts: formatInteger(rejection.attemptCount, locale),
        })}
      </ControlMono>
    );
  }
  return <ControlMono tone="muted">{t("admin.gtcc.diag.path.gateway.no_connection")}</ControlMono>;
}

/**
 * The reference's four-station chain, with the reference's two columns:
 * STATION and BEFUND. The third column this view used to add ("VERDIKT")
 * printed the same "nicht messbar" in every row and is gone — a column that
 * repeats its neighbour is not a finding (ABWEICHUNGEN, section "diag").
 *
 * The reference's own header names what this panel needs — "Fahrzeug wählen
 * → Kette prüfen" — so the selection this view was missing (SONDEN.md,
 * "no_selection") is not a deviation from "rows, not forms": that rule names
 * the three panels that make no request at all (Auth-Diagnose,
 * Berechtigungs-Prüfer, Rohpaket-Decoder); this probe already makes one, it
 * only lacked an identifier to send. Tenant and device id are typed in
 * (`docs/design/SHARED_COMPONENT_INVENTORY.md` has no canonical admin
 * tenant/device picker to reuse; an operator here already holds a specific
 * UUID rather than browsing a list — Sprint-Closeout entry filed for this
 * local input), submitted once, and the same identifier then drives both the
 * "device" (probe 1) and "gateway" (probes 2/16) stations, exactly as the
 * reference's single "Fahrzeug wählen" drives the whole chain.
 *
 * "Datenbank (Store)" and "Karte/App" still have no reading route at all:
 * their facts live under FORCE RLS and a platform read needs a new
 * cross-tenant SECURITY DEFINER function, which is an Owner decision outside
 * this package. The panel still prints no one-sentence verdict over the whole
 * chain: a verdict spanning measured and unmeasured stations would be an
 * invention.
 */
function PathProbePanel(): ReactNode {
  const { t } = useLocale();
  const [tenantId, setTenantId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [selected, setSelected] = useState<{ tenantId: string; deviceId: string } | null>(null);

  const deviceStateQuery = useQuery({
    queryKey: ["gtcc", "diag", "device-state", selected?.tenantId ?? "", selected?.deviceId ?? ""],
    queryFn: ({ signal }) => fetchDiagDeviceState(selected!.tenantId, selected!.deviceId, signal),
    enabled: selected !== null,
    ...DIAG_QUERY_OPTIONS,
  });
  const deviceState = dataOf(deviceStateQuery);
  const identifier =
    deviceState !== null && deviceState.outcome === "found" && deviceState.device !== null
      ? deviceState.device.identifier
      : null;

  const gatewayViewQuery = useQuery({
    queryKey: ["gtcc", "diag", "gateway-view", identifier ?? ""],
    queryFn: ({ signal }) => fetchDiagGatewayView(identifier!, signal),
    enabled: identifier !== null,
    ...DIAG_QUERY_OPTIONS,
  });
  const gatewayView = dataOf(gatewayViewQuery);

  const canSubmit = tenantId.trim() !== "" && deviceId.trim() !== "";
  const submit = useCallback(() => {
    if (tenantId.trim() === "" || deviceId.trim() === "") return;
    setSelected({ tenantId: tenantId.trim(), deviceId: deviceId.trim() });
  }, [tenantId, deviceId]);

  return (
    <ControlPanel
      testId="gtcc-diag-path"
      title={t("admin.gtcc.diag.path.title")}
      note={t("admin.gtcc.diag.path.note")}
    >
      <ControlRow
        testId="gtcc-diag-path-select-row"
        tone="muted"
        label={t("admin.gtcc.diag.path.select.label")}
        value={
          <span
            style={{
              display: "inline-flex",
              gap: "var(--gt-sys-space-2)",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              aria-label={t("admin.gtcc.diag.path.select.tenant_label")}
              data-testid="gtcc-diag-path-tenant-input"
              value={tenantId}
              onChange={(event) => setTenantId(event.currentTarget.value)}
              placeholder={t("admin.gtcc.diag.path.select.tenant_label")}
              style={PATH_INPUT_STYLE}
            />
            <input
              aria-label={t("admin.gtcc.diag.path.select.device_label")}
              data-testid="gtcc-diag-path-device-input"
              value={deviceId}
              onChange={(event) => setDeviceId(event.currentTarget.value)}
              placeholder={t("admin.gtcc.diag.path.select.device_label")}
              style={PATH_INPUT_STYLE}
            />
            <ControlTextAction
              testId="gtcc-diag-path-submit"
              label={t("admin.gtcc.diag.path.select.submit")}
              onActivate={submit}
              disabled={!canSubmit}
              brackets
            />
          </span>
        }
      />
      <ControlTableWrap>
        <table style={TABLE_STYLE}>
          <caption style={{ captionSide: "top", textAlign: "start", display: "none" }}>
            {t("admin.gtcc.diag.path.caption")}
          </caption>
          <thead>
            <tr>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.diag.path.col.station")}
              </th>
              <th scope="col" style={TH_STYLE}>
                {t("admin.gtcc.diag.path.col.finding")}
              </th>
            </tr>
          </thead>
          <tbody>
            {DIAG_PATH_STATIONS.map((station) => (
              <tr key={station} data-testid={`gtcc-diag-path-${station}`}>
                <td style={HOST_CELL_STYLE}>{t(`admin.gtcc.diag.path.station.${station}`)}</td>
                <td style={TD_STYLE}>
                  {station === "device" ? (
                    <DevicePathFinding
                      selected={selected !== null}
                      query={deviceStateQuery}
                      device={deviceState}
                    />
                  ) : station === "gateway" ? (
                    <GatewayPathFinding
                      deviceOutcome={deviceState?.outcome ?? null}
                      identifier={identifier}
                      query={gatewayViewQuery}
                      gateway={gatewayView}
                    />
                  ) : (
                    <ControlNotMeasurable label={t("admin.gtcc.diag.no_endpoint")} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ControlTableWrap>
      <ControlNote testId="gtcc-diag-path-verdict">
        {t("admin.gtcc.diag.path.no_verdict")}
      </ControlNote>
    </ControlPanel>
  );
}

/* ------------------------------------------------ 2 · Datenqualitäts-Prüfer */

/**
 * The "3-km-Lehre als Werkzeug" panel. The four checks exist as *ingest*
 * filters (`services/api/src/gateway/`), but no admin route reports their
 * outcome per vehicle, so the rows keep their structure and their honest state.
 */
function DataQualityPanel(): ReactNode {
  const { t } = useLocale();
  return (
    <ControlPanel
      testId="gtcc-diag-dq"
      title={t("admin.gtcc.diag.dq.title")}
      note={t("admin.gtcc.diag.dq.note")}
    >
      <Stack gap={0}>
        {DIAG_QUALITY_CHECKS.map((check) => (
          <ControlRow
            key={check}
            testId={`gtcc-diag-dq-${check}`}
            tone="muted"
            label={t(`admin.gtcc.diag.dq.check.${check}`)}
            value={<ControlNotMeasurable label={t("admin.gtcc.diag.no_endpoint")} />}
          />
        ))}
      </Stack>
    </ControlPanel>
  );
}

/* ------------------------------------------------- 3 · Recent Errors (P19) */

function RecentErrorsPanel(props: {
  errors: DiagRecentErrorsDto | null;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  /* Eine Antwort ohne Liste ist keine gemessene Null, sondern gar keine
     Messung — nur eine wirklich leere Liste heisst „keine Fehler" (P9). */
  const measured = Array.isArray(props.errors?.items);
  const items = asList(props.errors?.items);
  const shown = items.slice(0, DIAG_RECENT_VISIBLE);

  return (
    <ControlPanel
      testId="gtcc-diag-recent"
      title={t("admin.gtcc.diag.recent.title")}
      note={t("admin.gtcc.diag.recent.note", {
        limit: formatInteger(DIAG_RECENT_ERRORS_LIMIT, props.locale),
      })}
    >
      {!measured ? (
        <ControlRow
          testId="gtcc-diag-recent-unmeasured"
          tone="muted"
          label={t("admin.gtcc.diag.recent.title")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.not_measurable")} />}
        />
      ) : items.length === 0 ? (
        <ControlNote testId="gtcc-diag-recent-empty">
          {t("admin.gtcc.diag.recent.empty")}
        </ControlNote>
      ) : (
        <>
          <ControlTableWrap>
            <table style={TABLE_STYLE}>
              <caption style={{ captionSide: "top", textAlign: "start", display: "none" }}>
                {t("admin.gtcc.diag.recent.caption")}
              </caption>
              <thead>
                <tr>
                  <th scope="col" style={TH_STYLE}>
                    {t("admin.gtcc.diag.recent.col.time")}
                  </th>
                  <th scope="col" style={TH_STYLE}>
                    {t("admin.gtcc.diag.recent.col.route")}
                  </th>
                  <th scope="col" style={TH_STYLE}>
                    {t("admin.gtcc.diag.recent.col.message")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item, index) => (
                  <tr key={`${item.time}-${index}`} data-testid="gtcc-diag-recent-row">
                    <td style={HOST_CELL_STYLE}>{item.time}</td>
                    <td style={TD_STYLE}>
                      <ControlMono>
                        {item.route ?? t("admin.gtcc.diag.recent.no_route")}
                      </ControlMono>
                    </td>
                    <td style={TD_STYLE}>{item.msg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ControlTableWrap>
          {items.length > shown.length ? (
            <ControlNote testId="gtcc-diag-recent-truncated">
              {t("admin.gtcc.diag.recent.truncated", {
                shown: formatInteger(shown.length, props.locale),
                total: formatInteger(items.length, props.locale),
              })}
            </ControlNote>
          ) : null}
        </>
      )}
    </ControlPanel>
  );
}

/* ------------------------------------------------------- 4 · Auth-Diagnose */

/**
 * The reference asks one question per account — "Konto da? aktiv? Rollen?
 * Tenant? Redirect-Ziel?" — as a single row ending in "prüfen →".
 *
 * There is no auth-diagnosis route in `services/api/src`, so this probe cannot
 * answer and the row carries no activator: an affordance that leads nowhere is
 * worse than none. The former text field is gone with it — a field nobody may
 * type into is the same dead end with more furniture (ABWEICHUNGEN, "diag").
 *
 * What S2 did add is the one part of the question that has a source: where a
 * host lands (`/v1/admin/gtcc/diag/domain-routing`, probes 11/24). It needs a
 * host, which this view does not collect, and its own payload states that the
 * redirect target after a sign-in is not a server fact. The note names both
 * rather than letting the panel imply a checked login path.
 */
function AuthDiagnosisPanel(): ReactNode {
  const { t } = useLocale();
  return (
    <ControlPanel testId="gtcc-diag-auth" title={t("admin.gtcc.diag.auth.title")}>
      <Stack gap={0}>
        <ControlRow
          testId="gtcc-diag-auth-question"
          tone="muted"
          label={t("admin.gtcc.diag.auth.question")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.no_endpoint")} />}
        />
        <ControlRow
          testId="gtcc-diag-auth-routing"
          tone="muted"
          label={t("admin.gtcc.diag.auth.routing")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.no_selection")} />}
        />
      </Stack>
      <ControlNote testId="gtcc-diag-auth-note">{t("admin.gtcc.diag.auth.note")}</ControlNote>
    </ControlPanel>
  );
}

/* ------------------------------------------------- 5 · Berechtigungs-Prüfer */

/**
 * "Rolle wählen → zeigt je Fläche LESEN/SCHREIBEN/NICHTS" of the reference —
 * one row with an actual selection control, not a row per role of the
 * catalog (TRIAGE D-GTCC-VIEW-TRIAGE1, "diag" #2). The check itself is
 * unchanged and still local (`permission-matrix.ts` over `access/roles.ts`);
 * it makes no request, which is also why the head may not promise an audit-log
 * entry for it.
 */
function PermissionCheckerPanel(): ReactNode {
  const { t, locale } = useLocale();
  const [check, setCheck] = useState<PermissionRoleCheck | null>(null);

  const run = useCallback((roleId: string) => setCheck(checkPermissionRole(roleId)), []);

  return (
    <ControlPanel testId="gtcc-diag-perm" title={t("admin.gtcc.diag.perm.title")}>
      <ControlRow
        testId="gtcc-diag-perm-select-row"
        tone="muted"
        label={t("admin.gtcc.diag.perm.question")}
        value={
          <select
            aria-label={t("admin.gtcc.diag.perm.question")}
            data-testid="gtcc-diag-perm-select"
            value={check?.roleId ?? ""}
            onChange={(event) => {
              const roleId = event.currentTarget.value;
              if (roleId !== "") run(roleId);
            }}
            style={{
              background: "var(--gt-sys-surface)",
              border: "1px solid var(--gt-sys-border)",
              borderRadius: "var(--gt-sys-radius-sm)",
              color: "var(--gt-sys-text)",
              padding: "var(--gt-sys-space-2)",
              fontFamily: "var(--gt-sys-font-family-mono)",
            }}
          >
            <option value="">{t("admin.gtcc.diag.perm.select_placeholder")}</option>
            {PERMISSION_ROLE_IDS.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        }
      />
      {check === null ? (
        <ControlNote testId="gtcc-diag-perm-idle">{t("admin.gtcc.diag.perm.idle")}</ControlNote>
      ) : (
        <>
          <ControlRow
            testId="gtcc-diag-perm-last"
            tone={check.writeAreaCount === 0 ? "ok" : "warn"}
            label={t("admin.gtcc.diag.perm.last", { role: check.roleId })}
            value={
              <ControlMono>
                {t("admin.gtcc.diag.perm.last_value", {
                  write: formatInteger(check.writeAreaCount, locale),
                  read: formatInteger(check.readOnlyAreaCount, locale),
                })}
              </ControlMono>
            }
          />
          <Stack gap={0}>
            {check.areas.map((area) => (
              <ControlRow
                key={area.areaId}
                testId={`gtcc-diag-perm-area-${area.areaId}`}
                tone={toneOfAccess(area.access)}
                label={t(`admin.gtcc.diag.perm.area.${area.areaId}`)}
                value={<ControlMono>{t(`admin.gtcc.diag.perm.access.${area.access}`)}</ControlMono>}
              />
            ))}
          </Stack>
          <ControlNote testId="gtcc-diag-perm-source">
            {t("admin.gtcc.diag.perm.source", {
              areas: formatInteger(PERMISSION_AREAS.length, locale),
            })}
          </ControlNote>
        </>
      )}
    </ControlPanel>
  );
}

/* ----------------------------------------------------- 6 · Rohpaket-Decoder */

/**
 * "Codec8-Paket (hex) einfügen → lesbar zerlegt  decodieren →" of the
 * reference: one row, no text area, no button (ABWEICHUNGEN, "diag").
 *
 * "Einfügen" is the clipboard, so the row reads it there instead of holding a
 * field for it. The decode itself is unchanged and stays local
 * (`codec8-decode.ts`): nothing is sent anywhere, which the note says out loud
 * because a pasted packet is operator data.
 *
 * A clipboard the browser refuses (no permission, no secure context, no API at
 * all) is a state of its own — the panel says "nicht lesbar" instead of
 * reporting an empty packet the operator never supplied (P10).
 */
function PacketDecoderPanel(): ReactNode {
  const { t, locale } = useLocale();
  const [result, setResult] = useState<Codec8DecodeResult | null>(null);
  const [clipboardReadable, setClipboardReadable] = useState(true);

  const decode = useCallback(() => {
    const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.readText !== "function") {
      setResult(null);
      setClipboardReadable(false);
      return;
    }
    void clipboard.readText().then(
      (text) => {
        setClipboardReadable(true);
        setResult(decodeCodec8Frame(text));
      },
      () => {
        setResult(null);
        setClipboardReadable(false);
      },
    );
  }, []);

  return (
    <ControlPanel
      testId="gtcc-diag-codec"
      title={t("admin.gtcc.diag.codec.title")}
      note={t("admin.gtcc.diag.codec.question")}
    >
      <ControlActionRow
        testId="gtcc-diag-codec-open"
        tone={result === null ? "muted" : "info"}
        label={t("admin.gtcc.diag.codec.open")}
        value={<ControlMono tone="muted">{t("admin.gtcc.diag.action.decode")}</ControlMono>}
        onActivate={decode}
      />
      {!clipboardReadable ? (
        <ControlRow
          testId="gtcc-diag-codec-unavailable"
          tone="muted"
          label={t("admin.gtcc.diag.codec.clipboard")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.codec.unavailable")} />}
        />
      ) : result === null ? (
        <ControlNote testId="gtcc-diag-codec-idle">{t("admin.gtcc.diag.codec.idle")}</ControlNote>
      ) : !result.ok ? (
        <ControlRow
          testId="gtcc-diag-codec-error"
          tone="crit"
          label={t(`admin.gtcc.diag.codec.reason.${result.reason}`)}
          value={<ControlMono tone="crit">{result.detail}</ControlMono>}
        />
      ) : (
        <Stack gap={0}>
          <ControlRow
            testId="gtcc-diag-codec-frame"
            tone="ok"
            label={t("admin.gtcc.diag.codec.frame")}
            value={
              <ControlMono>
                {t("admin.gtcc.diag.codec.frame_value", {
                  codec: result.codec,
                  bytes: formatInteger(result.frameBytes, locale),
                  records: formatInteger(result.records.length, locale),
                })}
              </ControlMono>
            }
          />
          {result.records.map((record, index) => (
            <div key={`${record.timestampIso}-${index}`} data-testid="gtcc-diag-codec-record">
              <ControlRow
                tone="info"
                label={t("admin.gtcc.diag.codec.time")}
                value={<ControlMono>{record.timestampIso}</ControlMono>}
              />
              <ControlRow
                tone="info"
                label={t("admin.gtcc.diag.codec.position")}
                value={
                  <ControlMono>
                    {t("admin.gtcc.diag.codec.position_value", {
                      lat: record.latitudeDeg.toFixed(6),
                      lon: record.longitudeDeg.toFixed(6),
                      sats: formatInteger(record.satellites, locale),
                    })}
                  </ControlMono>
                }
              />
              <ControlRow
                tone={record.ignitionOn === null ? "muted" : record.ignitionOn ? "ok" : "warn"}
                label={t("admin.gtcc.diag.codec.ignition")}
                value={
                  record.ignitionOn === null ? (
                    <ControlNotMeasurable label={t("admin.gtcc.diag.codec.ignition_unknown")} />
                  ) : (
                    <ControlMono>
                      {record.ignitionOn
                        ? t("admin.gtcc.diag.codec.ignition_on")
                        : t("admin.gtcc.diag.codec.ignition_off")}
                    </ControlMono>
                  )
                }
              />
              <ControlRow
                tone="info"
                label={t("admin.gtcc.diag.codec.io")}
                value={
                  <ControlMono>
                    {record.io.map((io) => `${io.id}=${io.value}`).join(" · ")}
                  </ControlMono>
                }
              />
            </div>
          ))}
        </Stack>
      )}
      {/* Nicht `…-codec-note`: diesen Namen traegt seit der Frage im Panel-Kopf
          die Notiz von `ControlPanel` selbst — zwei gleiche Namen sind keiner. */}
      <ControlNote testId="gtcc-diag-codec-privacy">{t("admin.gtcc.diag.codec.note")}</ControlNote>
    </ControlPanel>
  );
}

/* ------------------------------------------------------- 7 · Weitere Sonden */

/**
 * The reference's sixth panel — the one the old build replaced with a
 * "Service-/Config-Snapshot" card (ABWEICHUNGEN, "diag"). The snapshot is not
 * lost with that card: its payload is still fetched and still copied verbatim,
 * from this panel's last row ("kopieren →"), exactly as the reference lists it.
 *
 * Die Geocode-Zeile (Sonde 14) bleibt ehrlich „nicht messbar": ihre Tatsachen
 * stehen im `geocode_journal` unter FORCE RLS, eine plattformweite Lesung
 * braucht also eine neue SECURITY-DEFINER-Funktion. Das ist eine
 * Owner-Entscheidung und nicht Teil dieses Pakets
 * (`docs/design/gtcc-screens/A-GTCC-VIEW-DIAG2/SONDEN.md`, Abschnitt
 * „Offene Owner-Entscheidung"), also zeigt die Zeile das Fehlen der Quelle
 * statt einer erfundenen Null.
 */
function MoreProbesPanel(props: {
  mailHealth: DiagMailHealthDto | null;
  snapshot: DiagSnapshotDto | null;
  links: DiagLinksDto | null;
  onOpenMail: () => void;
}): ReactNode {
  const { t } = useLocale();
  const testSendEnabled = props.mailHealth?.readiness?.testSendEnabled === true;
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");

  const copyPayload = useCallback(() => {
    const clipboard = typeof navigator === "undefined" ? undefined : navigator.clipboard;
    if (clipboard === undefined || typeof clipboard.writeText !== "function") {
      setCopyState("failed");
      return;
    }
    const payload = JSON.stringify({ snapshot: props.snapshot, links: props.links }, null, 2);
    void clipboard.writeText(payload).then(
      () => setCopyState("done"),
      () => setCopyState("failed"),
    );
  }, [props.snapshot, props.links]);

  return (
    <ControlPanel testId="gtcc-diag-probes" title={t("admin.gtcc.diag.probes.title")}>
      <Stack gap={0}>
        <ControlRow
          testId="gtcc-diag-probes-geocode"
          tone="muted"
          label={t("admin.gtcc.diag.probes.geocode")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.no_endpoint")} />}
        />
        {testSendEnabled ? (
          <ControlActionRow
            testId="gtcc-diag-probes-mail"
            tone="info"
            label={t("admin.gtcc.diag.probes.mail")}
            onActivate={props.onOpenMail}
          />
        ) : (
          <ControlRow
            testId="gtcc-diag-probes-mail"
            tone="muted"
            label={t("admin.gtcc.diag.probes.mail")}
            value={<ControlMono tone="muted">{t("admin.gtcc.diag.probes.mail_off")}</ControlMono>}
          />
        )}
        <ControlRow
          testId="gtcc-diag-probes-trace"
          tone="muted"
          label={t("admin.gtcc.diag.probes.trace")}
          value={<ControlNotMeasurable label={t("admin.gtcc.diag.no_endpoint")} />}
        />
        <ControlRow
          testId="gtcc-diag-probes-errors"
          tone="info"
          label={t("admin.gtcc.diag.probes.errors")}
          value={<ControlMono tone="muted">{t("admin.gtcc.diag.probes.in_view")}</ControlMono>}
        />
        <ControlRow
          testId="gtcc-diag-probes-smoke"
          tone="info"
          label={t("admin.gtcc.diag.probes.smoke")}
          value={<ControlMono tone="muted">{t("admin.gtcc.diag.probes.in_view")}</ControlMono>}
        />
        <ControlActionRow
          testId="gtcc-diag-probes-snapshot"
          tone="info"
          label={t("admin.gtcc.diag.probes.snapshot")}
          value={<ControlMono tone="muted">{t("admin.gtcc.diag.action.copy")}</ControlMono>}
          onActivate={copyPayload}
        />
      </Stack>
      {copyState === "idle" ? null : (
        <ControlNote testId="gtcc-diag-probes-snapshot-state">
          {copyState === "done"
            ? t("admin.gtcc.diag.probes.copy_done")
            : t("admin.gtcc.diag.probes.copy_failed")}
        </ControlNote>
      )}
      <ControlNote testId="gtcc-diag-probes-note">{t("admin.gtcc.diag.probes.note")}</ControlNote>
    </ControlPanel>
  );
}

/* --------------------------------------------------- 8 · Smoke Dashboard (P19) */

function SmokePanel(props: {
  queries: ReturnType<typeof useGtccSmokeQueries>;
  locale: Parameters<typeof formatInteger>[1];
}): ReactNode {
  const { t } = useLocale();
  const tally = tallySmokeQueries(props.queries);

  return (
    <ControlPanel
      testId="gtcc-diag-smoke"
      title={t("admin.gtcc.diag.smoke.title")}
      note={t("admin.gtcc.diag.smoke.tally", {
        green: formatInteger(tally.green, props.locale),
        yellow: formatInteger(tally.yellow, props.locale),
        red: formatInteger(tally.red, props.locale),
      })}
    >
      <Stack gap={0}>
        {GTCC_SMOKE_PROBES.map((probe: GtccSmokeProbeKey) => {
          const query = props.queries[probe];
          /* Nur die drei Zustaende der alten Konsole; „laeuft noch" ist kein
             Zustand der Sonde, sondern die Abwesenheit einer Messung. */
          const state: "ok" | "warn" | "crit" = query.isError
            ? "crit"
            : toneOfSmoke(probeTone(probe, props.queries));
          return (
            <ControlRow
              key={probe}
              testId={`gtcc-diag-smoke-${probe}`}
              tone={query.isPending ? "muted" : state}
              label={t(`admin.gtcc.diag.smoke.probe.${probe}`)}
              value={
                query.isPending ? (
                  <ControlNotMeasurable label={t("admin.gtcc.diag.not_measurable")} />
                ) : (
                  <ControlMono>{t(`admin.gtcc.diag.smoke.state.${state}`)}</ControlMono>
                )
              }
            />
          );
        })}
      </Stack>
      <ControlNote testId="gtcc-diag-smoke-hint">{t("admin.gtcc.diag.smoke.note")}</ControlNote>
    </ControlPanel>
  );
}

/* Re-export only the view; the helpers above stay module-private on purpose. */
export default DiagView;
